import {
  BottomSheet,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { CLIPBOARD_SCORE } from "@src/appV2/ClipboardScore/constants";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCallback } from "react";
import { useRouteMatch } from "react-router-dom";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { LoadingButton } from "../../components/LoadingButton";
import { ShiftBlockStatus, useUpdateShiftBlock } from "./api/useUpdateBlockShift";
import { SHIFT_BLOCK_ACTIONS } from "./utils";

interface Props {
  modalState: UseModalState;
  shiftBlockId: string;
  facilityId: string;
  totalShifts: number;
  onConfirmCancellation: () => void;
}

export function CancelShiftBlockBottomSheet(props: Props) {
  const { modalState, shiftBlockId, facilityId, totalShifts, onConfirmCancellation } = props;
  const { showErrorToast, showInfoToast } = useToast();
  const { path: currentRouterUrl } = useRouteMatch();

  const { mutateAsync: mutateBookShiftBlock, isLoading: isLoadingBookShiftBlock } =
    useUpdateShiftBlock({
      shiftBlockId,
    });

  const handleKeepClicked = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.KEEP_BLOCK_TAPPED,
      shiftBlockId,
      facilityId,
      totalShifts,
      location: currentRouterUrl,
    });
    modalState.closeModal();
  }, [currentRouterUrl, facilityId, modalState, shiftBlockId, totalShifts]);

  const handleCancelClicked = useCallback(async () => {
    try {
      await mutateBookShiftBlock({
        data: {
          type: "shift-block",
          id: shiftBlockId,
          attributes: {
            status: ShiftBlockStatus.CANCELLED,
          },
        },
      });
      logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
        action: SHIFT_BLOCK_ACTIONS.YES_CANCEL_TAPPED,
        shiftBlockId,
        facilityId,
        totalShifts,
        location: currentRouterUrl,
      });
      showInfoToast("Shift block canceled successfully.");
      onConfirmCancellation();
    } catch {
      showErrorToast("Something went wrong while canceling the shift block.");
    } finally {
      modalState.closeModal();
    }
  }, [
    currentRouterUrl,
    facilityId,
    modalState,
    mutateBookShiftBlock,
    onConfirmCancellation,
    shiftBlockId,
    showErrorToast,
    showInfoToast,
    totalShifts,
  ]);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="horizontal">
          <Button fullWidth variant="outlined" size="large" onClick={handleKeepClicked}>
            Keep block
          </Button>
          <LoadingButton
            fullWidth
            variant="destructive"
            size="large"
            isLoading={isLoadingBookShiftBlock}
            onClick={handleCancelClicked}
          >
            Yes, cancel
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={4} alignItems="center">
          <Text bold variant="h4" sx={{ textAlign: "center" }}>
            Cancel This Block
          </Text>
          <Illustration type="cancellation" />
          <Text align="center">
            This cancellation will remove you from all future shifts in this block and penalize your{" "}
            {CLIPBOARD_SCORE}. Are you sure you want to cancel this block?
          </Text>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
